import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@xanectech/ui/lib';
import { useAuth } from '@xanectech/auth';
import { createLogger } from '@xanectech/lib';
import { CompanyService } from '../services/CompanyService';
import type { CompanyUser } from '@xanectech/types';

const logger = createLogger('Data - useCompanyUsers');

// Hook to fetch users for a specific company
export const useCompanyUsers = (companyId?: string) => {
  return useQuery({
    queryKey: ['company-users', companyId],
    queryFn: () => {
      if (!companyId) throw new Error('Company ID is required');
      return CompanyService.getCompanyUsers(companyId);
    },
    enabled: !!companyId,
    staleTime: 0,
    gcTime: 0,
  });
};

// Hook to fetch company user statistics
export const useCompanyUserStats = (companyId?: string) => {
  return useQuery({
    queryKey: ['company-user-stats', companyId],
    queryFn: () => {
      if (!companyId) throw new Error('Company ID is required');
      return CompanyService.getCompanyUserStats(companyId);
    },
    enabled: !!companyId,
  });
};

// Hook to get current user's company ID for company admins
export const useCurrentUserCompany = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['current-user-company', user?.id],
    queryFn: () => {
      if (!user?.id) {
        logger.info('useCurrentUserCompany: User not authenticated', {
          user_id: user?.id,
        });
        throw new Error('User not authenticated');
      }

      logger.info('useCurrentUserCompany: Fetching company for user:', {
        user_id: user.id,
      });
      return CompanyService.getCurrentUserCompany(user.id as string);
    },
    enabled: !!user?.id,
  });
};

// Hook to invite a new user to company (legacy)
export const useInviteCompanyUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: CompanyService.inviteUser,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['company-users', variables.company_id as string],
      });
      void queryClient.invalidateQueries({
        queryKey: ['company-user-stats', variables.company_id as string],
      });
      toast({
        title: 'User Invited',
        description: 'User invitation has been sent successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to invite user: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

// Hook to create a new company user (sends set-password email, no password required)
export const useCreateCompanyUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: CompanyService.createCompanyUser,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['company-users', variables.company_id as string],
      });
      void queryClient.invalidateQueries({
        queryKey: ['company-user-stats', variables.company_id as string],
      });
      toast({
        title: 'User Created',
        description: 'User has been created and will receive an email to set their password.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to create user: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

// Hook to update company user
export const useUpdateCompanyUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      updates,
    }: {
      userId: string;
      companyId: string;
      updates: Partial<CompanyUser>;
    }) => CompanyService.updateCompanyUser(userId, companyId, updates),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['company-users', variables.companyId as string],
      });
      toast({
        title: 'User Updated',
        description: 'User information has been successfully updated.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update user: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};

// Hook to suspend/activate company user
export const useToggleCompanyUserStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      isActive,
    }: {
      userId: string;
      companyId: string;
      isActive: boolean;
    }) => CompanyService.toggleUserStatus(userId, companyId, isActive),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['company-users', variables.companyId as string],
      });
      void queryClient.invalidateQueries({
        queryKey: ['company-user-stats', variables.companyId as string],
      });
      toast({
        title: variables.isActive ? 'User Activated' : 'User Suspended',
        description: `User has been successfully ${variables.isActive ? 'activated' : 'suspended'}.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update user status: ${error.message}`,
        variant: 'destructive',
      });
    },
  });
};
